import type { MapDefinition, Obstacle, Point } from '../../../shared/game/world-types.js';
import { pointSegmentDistance } from '../../../shared/navigation/geometry.js';
import { Surfaces } from '../../audio/surfaces.js';
import { terrainGradient, terrainHeight } from '../../../shared/game/terrain.js';

const CELL = 16;
const ramp = (value: number, from: number, to: number) => Math.min(1, Math.max(0, (value - from) / (to - from)));
export type Habitat = 'rabbit' | 'crow' | 'gull' | 'deer' | 'sheep' | 'crab' | 'butterfly';
type Biome = NonNullable<MapDefinition['scenery']>['districts'][number]['biome'];
/** How welcome each kind of ground is, 0 meaning never. Ground outside every district is meadow. */
const WELCOME: Record<Exclude<Habitat, 'gull' | 'crab'>, Record<Biome, number>> = {
  rabbit: { meadow: 1, farmland: .9, heath: .7, pine: .45, marsh: 0, sand: 0, cobble: 0, scree: 0 },
  crow: { meadow: 1, farmland: 1, heath: .7, pine: .4, marsh: .5, sand: .5, cobble: .6, scree: 0 },
  // Deer keep to the woods and their edges, sheep to the heath and the rough grazing, butterflies to flowery open ground.
  deer: { pine: 1, meadow: .45, heath: .25, marsh: .3, farmland: .15, sand: 0, cobble: 0, scree: 0 },
  sheep: { heath: 1, meadow: .55, farmland: .5, scree: .2, pine: 0, marsh: 0, sand: 0, cobble: 0 },
  butterfly: { meadow: 1, farmland: .8, heath: .6, marsh: .3, pine: .2, sand: 0, cobble: 0, scree: 0 },
};

/** What lies underfoot for wandering animals: solid scenery, the sea, caves, roads and the kind of ground they like. Presentation-side only. */
export class CritterGround {
  height(x: number, z: number) { return terrainHeight(this.map, x, z); }
  gradient(x: number, z: number) { return terrainGradient(this.map, x, z); }
  private cells = new Map<string, Obstacle[]>();
  private readonly surfaces: Surfaces;
  private readonly trails: Surfaces;
  private readonly caves: { points: Point[]; reach: number }[];
  private readonly civilians: Point[];

  constructor(private map: MapDefinition) {
    for (const obstacle of map.obstacles) {
      for (let cx = Math.floor((obstacle.x - obstacle.width / 2) / CELL); cx <= Math.floor((obstacle.x + obstacle.width / 2) / CELL); cx++) {
        for (let cz = Math.floor((obstacle.z - obstacle.depth / 2) / CELL); cz <= Math.floor((obstacle.z + obstacle.depth / 2) / CELL); cz++) {
          const key = `${cx},${cz}`, cell = this.cells.get(key);
          if (cell) cell.push(obstacle); else this.cells.set(key, [obstacle]);
        }
      }
    }
    this.surfaces = new Surfaces(map);
    // Trails are dirt too: the same query over the trails drawn as if they were roads.
    this.trails = new Surfaces({ ...map, scenery: map.scenery && { ...map.scenery, paths: map.scenery.paths.filter(path => path.kind === 'trail').map(path => ({ ...path, kind: 'road' as const })) } });
    this.caves = (map.scenery?.paths ?? []).filter(path => path.kind === 'cave').map(path => ({ points: path.points, reach: path.width / 2 + 3 }));
    this.civilians = map.civilians ?? [];
  }

  /** Whether an animal `margin` metres wide can stand here: on the map, out of the sea, clear of scenery, caves and the camp's residents. */
  free(x: number, z: number, margin = .5): boolean {
    const { width, depth, scenery } = this.map;
    if (Math.abs(x) > width / 2 - 4 || Math.abs(z) > depth / 2 - 4) return false;
    const shelf = scenery?.shoreline.find(s => z >= s.z - s.depth / 2 && z <= s.z + s.depth / 2);
    if (shelf && x < shelf.x + shelf.width / 2 + 1.5 + margin) return false;
    // A neighbouring cell is searched too, so margins never miss an obstacle that only just reaches over a cell border.
    const cx = Math.floor(x / CELL), cz = Math.floor(z / CELL);
    for (let i = cx - 1; i <= cx + 1; i++) for (let j = cz - 1; j <= cz + 1; j++) {
      for (const o of this.cells.get(`${i},${j}`) ?? []) {
        if (Math.abs(x - o.x) < o.width / 2 + margin && Math.abs(z - o.z) < o.depth / 2 + margin) return false;
      }
    }
    const point = { x, z };
    if (this.caves.some(({ points, reach }) => points.some((end, k) => k > 0 && pointSegmentDistance(point, points[k - 1], end) < reach))) return false;
    return !this.civilians.some(civilian => Math.hypot(civilian.x - x, civilian.z - z) < 2 + margin);
  }

  /** The x of the tide line at `z`; the map's west edge where there is no shore. */
  coastEdge(z: number): number {
    const shelf = this.map.scenery?.shoreline.find(s => z >= s.z - s.depth / 2 && z <= s.z + s.depth / 2);
    return shelf ? shelf.x + shelf.width / 2 : -this.map.width / 2;
  }

  /** Metres inland of the tide line at `z`, or Infinity on a map with no sea. */
  shoreDistance(x: number, z: number): number { return this.map.scenery?.shoreline.length ? x - this.coastEdge(z) : Infinity; }

  onRoad(x: number, z: number) { return this.surfaces.onRoad(x, z); }

  /** On a drawn trail (the narrower dirt paths between the roads). */
  onTrail(x: number, z: number) { return this.trails.onRoad(x, z); }

  biome(x: number, z: number): Biome {
    let best: Biome = 'meadow', closeness = Infinity;
    for (const district of this.map.scenery?.districts ?? []) {
      const ratio = Math.hypot(x - district.x, z - district.z) / district.radius;
      if (ratio < 1 && ratio < closeness) { closeness = ratio; best = district.biome; }
    }
    return best;
  }

  /** 0 for ground this species avoids; otherwise how much it likes it, used as a spawn odds weight. Open road counts for nothing to rabbits. */
  welcome(habitat: Habitat, x: number, z: number): number {
    // Gulls loaf on the strand: everything from the wet sand to a dozen metres inland, fading out to forty.
    if (habitat === 'gull') return 1 - ramp(this.shoreDistance(x, z), 12, 40);
    // Crabs scuttle on the sand between the surf and a few metres up the beach.
    if (habitat === 'crab') return ramp(this.shoreDistance(x, z), 1.4, 2.4) * (1 - ramp(this.shoreDistance(x, z), 7, 11));
    if ((habitat === 'rabbit' || habitat === 'deer' || habitat === 'sheep') && this.onRoad(x, z)) return 0;
    return WELCOME[habitat][this.biome(x, z)];
  }
}
